<script setup>
import { computed, ref } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { parseISO } from 'date-fns';
import { useStore } from 'vuex';
import { cloneObject } from 'utils';
import { useAnalytics } from 'src/composables/analytics';

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  filterKey: {
    type: String,
    required: true
  },
  filterCode: {
    type: String,
    required: true
  },
  value: {
    type: Array,
    required: false,
    default: null,
  }
});

const currentDate = ref(
  props.value?.map((d) => (typeof d === 'string' ? parseISO(d) : d)) ?? null
);

const filterValue = computed(() => {
  if (!currentDate.value) return null;
  return currentDate.value
    .map((date) => (date ? date.toISOString().slice(0, 10) : null))
    .filter(Boolean)
    .join(',');
});

const store = useStore();
const urlFilters = computed(() => store.getters.urlFilters);

const { trackEvent } = useAnalytics();

function dateChanged() {
  const url = cloneObject(urlFilters.value);

  if (filterValue.value) {
    url[props.filterKey] = filterValue.value;
  } else {
    delete url[props.filterKey];
  }

  store.dispatch('setUrlFilters', url);

  trackEvent('filter', 'toggle', {
    filterType: 'date_range',
    filterLabel: props.label,
    filterKey: props.filterKey,
    value: filterValue.value
  });
}
</script>

<template>
  <div>
    <VueDatePicker
      v-model="currentDate"
      :range="{ partialRange: false }"
      auto-apply
      :multi-calendars="{ solo: true }"
      :placeholder="label"
      :time-config="{ enableTimePicker: false }"
      :formats="{ input: 'yyyy/MM/dd' }"
      :input-attrs="{ hideInputIcon: true }"
      @update:model-value="dateChanged" />
  </div>
</template>

<style lang="less">
@import (reference) "~styles/includes/base";
@import '@vuepic/vue-datepicker/dist/main.css';

.dp__theme_light {
  --dp-background-color: @color-mono-100;
  --dp-text-color: @color-mono-0;
  --dp-hover-color: lighten(@color-light, 40%);
  --dp-hover-text-color: @color-mono-0;
  --dp-primary-color: @color-light;
  --dp-primary-text-color: @color-mono-100;
  --dp-border-color: @color-mono-70;
  --dp-menu-border-color: @color-mono-70;
  --dp-border-color-hover: @color-mono-70;
  --dp-border-color-focus: @color-mono-70;

  input.dp__input {
    &::placeholder {
      color: @color-text-base;
    }
  }
}
</style>
